import { Alert, Form, message } from "antd";
import { useCurrentUser } from "../../../../lib/hooks/useCurrentUser";
import {
  fetchUserById,
  fetchUserRoles,
  updateCurrentUser,
} from "../../../../lib/api/resources/users";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchCompanies } from "../../../../lib/api/resources/companies";
import { useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { UserFormValues } from "../../users/components/UserForm";
import { Button, Card, Input, Select } from "@rumbledirect/ui";

export const PendingUserForm = () => {
  const [form] = Form.useForm();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const userQuery = useQuery({
    queryKey: ["user", currentUser?.userId],
    queryFn: () => fetchUserById(currentUser!.userId),
    enabled: !!currentUser?.userId,
  });

  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const roles = await fetchUserRoles();
      return roles.map((role) => ({ label: role.name, value: role["@id"] }));
    },
    enabled: !!currentUser?.userId,
  });

  const dealershipsQuery = useQuery({
    queryKey: ["dealerships"],
    queryFn: async () => {
      const companies = await fetchCompanies({
        type: ["dealership"],
        pagination: false,
      });
      return companies?.member.map((company) => ({
        label: company.name,
        value: company["@id"],
      }));
    },
    enabled: !!currentUser?.userId,
  });

  useEffect(() => {
    if (userQuery.data) {
      form.setFieldsValue({
        firstName: userQuery.data.firstName,
        lastName: userQuery.data.lastName,
        role: userQuery.data.role?.iri,
      });
    }
  }, [userQuery.data, form]);

  const updateUserMutation = useMutation({
    mutationFn: (data: Partial<UserFormValues>) => updateCurrentUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      navigate("/");
    },
    onError: () => {
      message.error(
        "Er is een fout opgetreden bij het bijwerken van de gegevens.",
      );
    },
  });

  if (!currentUser) {
    return null;
  }

  return (
    <Card loading={userQuery.isLoading} className="w-full">
      <Alert
        className="mb-4"
        type="info"
        message="Om je account te activeren, missen we nog een aantal gegevens. Vul het onderstaande formulier in om het aanmaken van je account af te ronden."
      />
      <Form form={form} layout="vertical" onFinish={updateUserMutation.mutate}>
        <Form.Item
          label="Voornaam"
          name="firstName"
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Achternaam"
          name="lastName"
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Rol"
          name="role"
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
          hidden={!currentUser?.role?.isPending}
        >
          <Select
            placeholder="Selecteer een rol"
            loading={rolesQuery.isLoading}
            options={rolesQuery.data}
          />
        </Form.Item>
        <Form.Item
          label="Vestiging"
          name="dealership"
          rules={[{ required: true, message: "Dit veld is verplicht." }]}
          hidden={!currentUser?.role?.dealershipRequired}
        >
          <Select
            placeholder="Selecteer een vestiging"
            loading={dealershipsQuery.isLoading}
            options={dealershipsQuery.data}
          />
        </Form.Item>

        <Form.Item label={null}>
          <Button
            loading={updateUserMutation.isPending}
            type="primary"
            htmlType="submit"
          >
            Activeren
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
